// Browser error reporting (spec rapid-api/docs/superpowers/specs/
// 2026-10-13-client-error-reporting-design.md): a JavaScript crash in the
// admin is sent to content's POST /api/ddd/errors/client and lands in the
// same error tracking as server errors (the Errors page, source "Browser";
// the same error.* alerts).
//
// What is reported: an uncaught error (window "error"), an unhandled
// rejection whose reason is an Error, and a render crash the root error
// boundary catches (client-error-boundary.js). What is not (noise, or
// already recorded): ResizeObserver loop warnings, "Script error." and
// errors from other origins or browser extensions, a code chunk that no
// longer loads (after a release the old build's chunks are gone: a reload
// fixes it, and reporting it would raise an alert on every release),
// aborted/cancelled
// requests, and every API failure (axios errors, anything carrying a
// request id: those are in the Request log already), and rejections that
// are not Errors (the API layers reject with { error, status }).
//
// Bounds: each crash once per page load (name + message + first frame;
// and each Error object once), at most MAX_REPORTS_PER_PAGE per page load,
// none after the server answers 429. Nothing leaves the browser with a
// query string or a token-like path segment: the page is the path with ids
// and tokens replaced, and URLs in messages and stacks lose their query.
// The server redacts again (core redact).

import { getAccessToken } from '../../storage/auth'
import { decodeJwtClaims } from '../jwt'
import { edxrUserAgentHeader } from '../api.service'
import { isRequestID, lastFailedRequestID, requestIDOf, REQUEST_ID_HEADER } from './request-id'

export const CLIENT_ERRORS_PATH = 'api/ddd/errors/client'
export const MAX_REPORTS_PER_PAGE = 10
// The app content stores the reports under (its closed list).
export const CLIENT_APP = 'admin'
// A failed API call this recent is named in the report.
export const FAILED_REQUEST_WINDOW_MS = 60 * 1000

const LIMITS = { name: 64, message: 1000, stack: 8000, componentStack: 4000 }

const newPageLoadID = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID()
  } catch (e) {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const fresh = () => ({ sent: 0, stopped: false, keys: new Set(), objects: new WeakSet(), pageLoadID: newPageLoadID() })
let state = fresh()

// Forgets this page load's reports (tests).
export const resetClientReporter = () => { state = fresh() }

const cap = (s, n) => (typeof s === 'string' ? (s.length > n ? s.slice(0, n) : s) : '')

const EXTENSION = /^(?:chrome|moz|safari|safari-web|ms-browser)-extension:/i
const EXTENSION_ANYWHERE = /(?:chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i

// A URL's origin, or null when it has none we can read.
const originOf = url => {
  try {
    return new URL(url, window.location.href).origin
  } catch (e) {
    return null
  }
}

// URLs in s lose their query and fragment; a trailing :line:col stays.
export const scrubURLs = s => cap(s, 64 * 1024).replace(/[a-z][a-z0-9+.-]*:\/\/[^\s()]+/gi, u => {
  const pos = (u.match(/(?::\d+){1,2}$/) || [''])[0]
  const bare = pos ? u.slice(0, -pos.length) : u
  const cut = bare.search(/[?#]/)
  return (cut >= 0 ? bare.slice(0, cut) : bare) + pos
})

// The page, as reported: the path only, each segment that may be an id or a
// secret replaced (an invite key or reset token is never sent).
export const pagePath = pathname => {
  const path = typeof pathname === 'string' ? pathname.split(/[?#]/)[0] : ''
  const out = path.split('/').map(s => {
    if (s === '' || /^[a-z]+(?:[-_.][a-z]+)*$/.test(s)) return s
    if (/^\d+$/.test(s)) return ':n'
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return ':uuid'
    if (s.length >= 16 || /\d/.test(s) || /[%=+~]/.test(s)) return ':token'
    return s
  }).join('/')
  return cap(out || '/', 512)
}

const errorLike = v => !!v && typeof v === 'object' && typeof v.message === 'string' && (typeof v.name === 'string' || v instanceof Error)

// Whether an error (or what an error event says of it) is noise this
// reporter never sends. `event` is the window error event's fields.
// webpack's "Loading chunk 12 failed." / "Loading CSS chunk 3 failed.", and
// the browsers' words for a dynamic import that no longer exists.
const CHUNK_LOAD = /Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

export const isNoise = (error, event = {}) => {
  const message = String((error && error.message) || event.message || '')
  if (/ResizeObserver loop/i.test(message)) return true
  if (CHUNK_LOAD.test(message) || (error && error.name === 'ChunkLoadError')) return true
  // A cross-origin script's error is "Script error." with nothing else.
  if (!error && /^Script error\.?$/i.test(message.trim())) return true
  const file = event.filename || ''
  if (file && (EXTENSION.test(file) || originOf(file) !== window.location.origin)) return true
  if (error) {
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return true
    if (error.isAxiosError) return true
    if (requestIDOf(error)) return true
    if (typeof error.stack === 'string' && EXTENSION_ANYWHERE.test(error.stack)) return true
  }
  return false
}

const firstFrame = stack => {
  const lines = (typeof stack === 'string' ? stack : '').split('\n').map(l => l.trim())
  return lines.find(l => /^at\s|@/.test(l)) || ''
}

// The report body content accepts (ingest.go's clientReport).
export const buildReport = (error, { componentStack } = {}) => {
  const name = cap(typeof error.name === 'string' && error.name ? error.name : 'Error', LIMITS.name)
  return {
    app: CLIENT_APP,
    name,
    message: cap(scrubURLs(error.message || ''), LIMITS.message),
    stack: cap(scrubURLs(typeof error.stack === 'string' ? error.stack : ''), LIMITS.stack),
    componentStack: cap(scrubURLs(componentStack || ''), LIMITS.componentStack),
    path: pagePath(window.location.pathname),
    failedRequestID: lastFailedRequestID(FAILED_REQUEST_WINDOW_MS) || undefined,
    pageLoadID: state.pageLoadID,
  }
}

// The access token while it is still valid; never refreshed (a report must
// never start a sign-in redirect).
const usableToken = () => {
  try {
    const token = getAccessToken()
    const claims = decodeJwtClaims(token)
    return claims && claims.exp * 1000 > Date.now() ? token : null
  } catch (e) {
    return null
  }
}

const endpoint = () => `${process.env.REACT_APP_API_CONTENT}/${CLIENT_ERRORS_PATH}`

// Sends one report: fetch with keepalive (with the edxr-user-agent header
// and the token, so content knows the app, browser and person), else a
// beacon (no headers: an anonymous report naming its app in the body).
// Resolves with the report's request id (X-Request-ID), or null.
const send = async report => {
  const body = JSON.stringify(report)
  if (typeof fetch === 'function') {
    const headers = { 'Content-Type': 'application/json' }
    const ua = edxrUserAgentHeader()
    if (ua) headers['edxr-user-agent'] = ua
    const token = usableToken()
    if (token) headers.Authorization = token
    try {
      const res = await fetch(endpoint(), { method: 'POST', keepalive: true, credentials: 'omit', headers, body })
      if (res.status === 429) state.stopped = true
      const id = res.headers && typeof res.headers.get === 'function' ? res.headers.get(REQUEST_ID_HEADER) : null
      return isRequestID(id) ? id : null
    } catch (e) {
      // a browser that refuses this keepalive request: try a beacon
    }
  }
  try {
    if (navigator && typeof navigator.sendBeacon === 'function') {
      // text/plain keeps the beacon a simple request (no preflight).
      navigator.sendBeacon(endpoint(), new Blob([body], { type: 'text/plain' }))
    }
  } catch (e) {
    // nothing more to try
  }
  return null
}

// Reports one error. Resolves with the report's request id (the reference
// support can look up), or null when nothing was sent (noise, a repeat, the
// page's cap, or a failed send). Never throws.
export const reportClientError = (error, { componentStack, event } = {}) => {
  try {
    let err = error
    if (!errorLike(err)) {
      if (typeof err === 'string') err = { name: 'Error', message: err }
      else if (event && event.message) err = { name: 'Error', message: String(event.message) }
      else return Promise.resolve(null)
    }
    if (isNoise(errorLike(error) ? error : null, event || {})) return Promise.resolve(null)
    if (state.stopped || state.sent >= MAX_REPORTS_PER_PAGE) return Promise.resolve(null)
    if (typeof error === 'object' && error !== null) {
      if (state.objects.has(error)) return Promise.resolve(null)
      state.objects.add(error)
    }
    const key = `${err.name}|${err.message}|${firstFrame(err.stack)}`
    if (state.keys.has(key)) return Promise.resolve(null)
    state.keys.add(key)
    state.sent++
    return send(buildReport(err, { componentStack })).catch(() => null)
  } catch (e) {
    return Promise.resolve(null)
  }
}

// Listens for uncaught errors and unhandled rejections on win. Returns the
// function that stops listening.
export const installClientErrorReporter = (win = window) => {
  const onError = e => {
    reportClientError(e.error, { event: { message: e.message, filename: e.filename } })
  }
  const onRejection = e => {
    const reason = e.reason
    // Only an Error is a crash; the API layers reject with { error, status }
    // and the request's id (already in the Request log).
    if (!errorLike(reason)) return
    reportClientError(reason)
  }
  win.addEventListener('error', onError)
  win.addEventListener('unhandledrejection', onRejection)
  return () => {
    win.removeEventListener('error', onError)
    win.removeEventListener('unhandledrejection', onRejection)
  }
}
